import React from 'react';
import { formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { patientsApi } from '../../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  User, ShieldCheck, Stethoscope, FileText, FirstAid, CircleNotch, Buildings,
} from '@phosphor-icons/react';
import ICD10Search from '../ICD10Search';
import AddressValidation from '../AddressValidation';
import {
  PAYER_TYPES, MANAGED_CARE_PLANS, SECONDARY_PAYER_TYPES, OHIO_COUNTIES,
  PAYER_PROGRAMS, PROCEDURE_CODES, PLACE_OF_SERVICE, RELATIONSHIP_CODES,
  codesForProgram, isUnusualCodeProgramPair,
} from '../../constants/patientConstants';
import { getCountyFromZip } from '../../constants/ohioZipCounty';
import { countyToCodbTier } from '../../constants/ohioCountyCodb';

const SUFFIXES = ['none', 'Jr', 'Sr', 'II', 'III', 'IV'];

/**
 * Multi-row Payer Program × Procedure Code editor. Some patients are billed
 * under multiple combinations (e.g. SP + G0156 for Home Health Aide AND
 * SP + T1019 for Personal Care) — this widget lets the user manage them as
 * a list. The first entry mirrors the legacy single `payer_program` /
 * `procedure_code` fields so all existing claim/EDI builders that read
 * those single fields keep working unchanged.
 *
 * Each row carries a client-side `_uid` so React can track the right DOM
 * node when the user adds or removes a billing line. Without a stable
 * key, React reconciles by index — which is fine for display but causes
 * focus loss and stale-input bugs in an editable list when a middle row
 * is removed. The `_uid` is stripped before the form is submitted (see
 * the patient save flow in usePatients.js).
 */
const _newLineUid = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const PatientBillingLines = ({ formData, handleChange }) => {
  // Normalize so the form always has at least one row. Each row is
  // { _uid, payer_type, payer_program, procedure_code }. The first
  // entry mirrors the legacy single fields on every mutation.
  const rawLines = (formData.billing_lines && formData.billing_lines.length > 0)
    ? formData.billing_lines
    : [{
        payer_type: formData.payer_type || '',
        payer_program: formData.payer_program || '',
        procedure_code: formData.procedure_code || '',
      }];
  const lines = rawLines.map((ln) => (ln._uid ? ln : { ...ln, _uid: _newLineUid() }));

  const writeLines = (next) => {
    handleChange('billing_lines', next);
    const first = next[0] || { payer_type: '', payer_program: '', procedure_code: '' };
    handleChange('payer_type', first.payer_type || '');
    handleChange('payer_program', first.payer_program || '');
    handleChange('procedure_code', first.procedure_code || '');
  };

  const updateLine = (idx, field, value) => {
    const next = lines.map((ln, i) => {
      if (i !== idx) return ln;
      return { ...ln, [field]: value };
      // P2 (May 2026 Rhinobill parity): we no longer clear the procedure
      // code when the program changes — all code/program pairs are
      // allowed; unusual pairs surface as soft warnings via the hint
      // line below and via the claim's `warnings` field at submission.
    });
    writeLines(next);
  };

  const addLine = () =>
    writeLines([...lines, { _uid: _newLineUid(), payer_type: '', payer_program: '', procedure_code: '' }]);
  const removeLine = (idx) => {
    if (lines.length <= 1) return;
    writeLines(lines.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-3" data-testid="patient-billing-lines">
      {lines.map((line, idx) => (
        <div
          key={line._uid}
          className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end"
          data-testid={`patient-billing-line-${idx}`}
        >
          <div className="space-y-2">
            <Label>{idx === 0 ? 'Payer Type' : <span className="text-slate-500">Payer Type</span>}</Label>
            <Select value={line.payer_type || ''} onValueChange={(v) => updateLine(idx, 'payer_type', v)}>
              <SelectTrigger data-testid={`patient-payer-type-${idx}`}><SelectValue placeholder="Select payer" /></SelectTrigger>
              <SelectContent>
                {PAYER_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{idx === 0 ? 'Payer Program' : <span className="text-slate-500">Payer Program</span>}</Label>
            <Select value={line.payer_program || ''} onValueChange={(v) => updateLine(idx, 'payer_program', v)}>
              <SelectTrigger data-testid={`patient-payer-program-${idx}`}><SelectValue placeholder="Select program" /></SelectTrigger>
              <SelectContent>
                {PAYER_PROGRAMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{idx === 0 ? 'Procedure Code' : <span className="text-slate-500">Procedure Code</span>}</Label>
            <Select value={line.procedure_code || ''} onValueChange={(v) => updateLine(idx, 'procedure_code', v)}>
              <SelectTrigger data-testid={`patient-procedure-code-${idx}`}>
                <SelectValue placeholder={line.payer_program ? 'Select code' : 'Pick a program first'} />
              </SelectTrigger>
              <SelectContent>
                {codesForProgram(line.payer_program).map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {line.payer_program ? (
              isUnusualCodeProgramPair(line.procedure_code, line.payer_program) ? (
                <p
                  className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 leading-tight flex items-start gap-1"
                  data-testid={`patient-procedure-warning-${idx}`}
                >
                  <span aria-hidden="true">⚠</span>
                  <span>
                    <b>{line.procedure_code}</b> isn&apos;t the canonical code for{' '}
                    <b>{PAYER_PROGRAMS.find((p) => p.value === line.payer_program)?.label || line.payer_program}</b>{' '}
                    — fine for custom contracts; will surface as a soft warning on claims.
                  </span>
                </p>
              ) : (
                <p className="text-[11px] text-slate-500 leading-tight" data-testid={`patient-procedure-hint-${idx}`}>
                  Canonical pair for {PAYER_PROGRAMS.find((p) => p.value === line.payer_program)?.label || line.payer_program}.
                </p>
              )
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-slate-400 hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30"
            onClick={() => removeLine(idx)}
            disabled={lines.length <= 1}
            aria-label="Remove this billing line"
            data-testid={`patient-billing-line-remove-${idx}`}
          >
            <span className="text-lg leading-none">×</span>
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addLine}
        data-testid="patient-billing-line-add"
      >
        + Add another payer / program / code
      </Button>
      <p className="text-xs text-slate-500">
        Use multiple lines when a patient is billed under different payer
        types / programs / procedure codes — e.g. <span className="font-mono">PP / T1019</span> paid by
        <span className="font-mono"> ODA</span> and <span className="font-mono">SP / T1001</span> paid by
        <span className="font-mono"> ODM</span>. The first line is treated as the primary on existing
        claims and EDI submissions.
      </p>
    </div>
  );
};

const PatientFormBasicTab = ({ formData, handleChange }) => {
  // Memoize the address object passed to AddressValidation so it isn't
  // recreated on every keystroke in unrelated fields, which would force
  // the validation component to re-render.
  const validationAddress = React.useMemo(() => ({
    street_address: formData.address,
    city: formData.city,
    state: formData.state,
    zip_code: formData.zip_code,
  }), [formData.address, formData.city, formData.state, formData.zip_code]);

  return (
    <TabsContent value="basic" className="space-y-4 mt-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>First Name *</Label>
        <Input value={formData.first_name} onChange={(e) => handleChange('first_name', e.target.value)} required data-testid="patient-first-name" />
      </div>
      <div className="space-y-2">
        <Label>Last Name *</Label>
        <Input value={formData.last_name} onChange={(e) => handleChange('last_name', e.target.value)} required data-testid="patient-last-name" />
      </div>
    </div>
    <div className="grid grid-cols-3 gap-4">
      <div className="space-y-2">
        <Label>Middle Name</Label>
        <Input value={formData.middle_name} onChange={(e) => handleChange('middle_name', e.target.value)} data-testid="patient-middle-name" />
      </div>
      <div className="space-y-2">
        <Label>Suffix</Label>
        <Select value={formData.suffix} onValueChange={(v) => handleChange('suffix', v)}>
          <SelectTrigger data-testid="patient-suffix"><SelectValue placeholder="None" /></SelectTrigger>
          <SelectContent>
            {SUFFIXES.map((s) => (
              <SelectItem key={s} value={s}>{s === 'none' ? 'None' : s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Gender *</Label>
        <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
          <SelectTrigger data-testid="patient-gender"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="M">Male</SelectItem>
            <SelectItem value="F">Female</SelectItem>
            <SelectItem value="U">Unknown</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Date of Birth *</Label>
        <Input type="date" value={formData.date_of_birth} onChange={(e) => handleChange('date_of_birth', e.target.value)} required data-testid="patient-date-of-birth" />
      </div>
      <div className="space-y-2">
        <Label>Phone</Label>
        <Input type="tel" value={formData.phone} onChange={(e) => handleChange('phone', e.target.value)} placeholder="(555) 555-5555" data-testid="patient-phone" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>SSN</Label>
        <Input
          value={formData.ssn}
          onChange={(e) => handleChange('ssn', e.target.value.replace(/\D/g, '').slice(0, 9))}
          maxLength={9} placeholder="9 digits, no dashes" className="font-mono" type="password" data-testid="patient-ssn"
        />
      </div>
      <div className="space-y-2">
        <Label>Newborn</Label>
        <Select value={formData.is_newborn ? 'true' : 'false'} onValueChange={(v) => handleChange('is_newborn', v === 'true')}>
          <SelectTrigger data-testid="patient-newborn"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="false">No</SelectItem>
            <SelectItem value="true">Yes</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    <div className="space-y-2">
      <Label>Address Line 1</Label>
      <Input value={formData.address} onChange={(e) => handleChange('address', e.target.value)} data-testid="patient-address" />
    </div>
    <div className="space-y-2">
      <Label>Address Line 2</Label>
      <Input value={formData.address_line2} onChange={(e) => handleChange('address_line2', e.target.value)} placeholder="Apt, Suite, Unit..." data-testid="patient-address2" />
    </div>
    <div className="grid grid-cols-4 gap-4">
      <div className="space-y-2">
        <Label>City</Label>
        <Input value={formData.city} onChange={(e) => handleChange('city', e.target.value)} data-testid="patient-city" />
      </div>
      <div className="space-y-2">
        <Label>State</Label>
        <Input value={formData.state} onChange={(e) => handleChange('state', e.target.value)} data-testid="patient-state" />
      </div>
      <div className="space-y-2">
        <Label>ZIP</Label>
        <Input
          value={formData.zip_code}
          onChange={(e) => {
            const v = e.target.value;
            handleChange('zip_code', v);
            // Auto-fill county on a complete 5-digit Ohio ZIP. Only writes
            // when the user hasn't already picked a county manually — never
            // clobber an explicit selection. CODB tier flows from county
            // automatically via the badge below + backend recalc.
            if (v && v.length === 5 && !formData.county) {
              const suggested = getCountyFromZip(v);
              if (suggested) handleChange('county', suggested);
            }
          }}
          data-testid="patient-zip"
        />
      </div>
      <div className="space-y-2">
        <Label>County</Label>
        <Select value={formData.county} onValueChange={(v) => handleChange('county', v)}>
          <SelectTrigger data-testid="patient-county"><SelectValue placeholder="County" /></SelectTrigger>
          <SelectContent>
            {OHIO_COUNTIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
    {formData.county && countyToCodbTier(formData.county) && (
      <div
        className="flex items-center gap-2 text-xs text-slate-600 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2"
        data-testid="patient-codb-badge"
      >
        <span className="font-medium text-indigo-700">
          DODD CODB Tier {countyToCodbTier(formData.county)}
        </span>
        <span className="text-slate-500">
          {' — '}auto-set from {formData.county} County per rule 5123-9-14. Used to price DODD claims (HMPCR, HCBTR, etc.).
        </span>
      </div>
    )}
    <AddressValidation
      address={validationAddress}
      onAddressChange={(addr) => {
        handleChange('address', addr.street_address);
        handleChange('city', addr.city);
        handleChange('state', addr.state);
        handleChange('zip_code', addr.zip_code);
      }}
      showInline
      className="flex justify-end"
    />
  </TabsContent>
  );
};

const PatientFormInsuranceTab = ({ formData, handleChange }) => (
  <TabsContent value="insurance" className="space-y-4 mt-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Medicaid ID *</Label>
        <Input value={formData.medicaid_id} onChange={(e) => handleChange('medicaid_id', e.target.value)} className="font-mono" required data-testid="patient-medicaid-id" />
      </div>
      <div className="space-y-2">
        <Label>Medicare ID</Label>
        <Input value={formData.medicare_id} onChange={(e) => handleChange('medicare_id', e.target.value)} className="font-mono" data-testid="patient-medicare-id" />
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      {(formData.payer_type === 'ManagedCare' || formData.payer_type === 'MyCare') && (
        <div className="space-y-2">
          <Label>Managed Care Plan</Label>
          <Select value={formData.managed_care_plan} onValueChange={(v) => handleChange('managed_care_plan', v)}>
            <SelectTrigger><SelectValue placeholder="Select plan" /></SelectTrigger>
            <SelectContent>
              {MANAGED_CARE_PLANS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>

    <div className="border-t pt-4">
      <h4 className="font-medium mb-3 text-sm text-slate-700">Sandata EVV / Billing Details</h4>
      <PatientBillingLines formData={formData} handleChange={handleChange} />
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="space-y-2">
          <Label>Eligibility Start Date</Label>
          <Input type="date" value={formData.payer_effective_start} onChange={(e) => handleChange('payer_effective_start', e.target.value)} data-testid="patient-elig-start" />
        </div>
        <div className="space-y-2">
          <Label>Eligibility End Date</Label>
          <Input type="date" value={formData.payer_effective_end} onChange={(e) => handleChange('payer_effective_end', e.target.value)} data-testid="patient-elig-end" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-4">
        <div className="space-y-2">
          <Label>Place of Service</Label>
          <Select value={formData.place_of_service} onValueChange={(v) => handleChange('place_of_service', v)}>
            <SelectTrigger data-testid="patient-pos"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {PLACE_OF_SERVICE.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Relationship to Subscriber</Label>
          <Select value={formData.relationship_to_subscriber} onValueChange={(v) => handleChange('relationship_to_subscriber', v)}>
            <SelectTrigger data-testid="patient-relationship"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {RELATIONSHIP_CODES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>

    <div className="border-t pt-4">
      <h4 className="font-medium mb-3 flex items-center gap-2"><Buildings size={16} /> Secondary Insurance</h4>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Secondary Payer</Label>
          <Select value={formData.secondary_payer_type} onValueChange={(v) => handleChange('secondary_payer_type', v)}>
            <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
            <SelectContent>
              {SECONDARY_PAYER_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Secondary ID</Label>
          <Input value={formData.secondary_insurance_id} onChange={(e) => handleChange('secondary_insurance_id', e.target.value)} className="font-mono" />
        </div>
      </div>
    </div>
  </TabsContent>
);

const PatientFormClinicalTab = ({
  formData, handleChange, npiSearchResults, npiSearching, showNpiResults,
  handleNpiSearch, handleSelectNpi,
}) => (
  <TabsContent value="clinical" className="space-y-4 mt-4">
    <div className="space-y-4">
      <h4 className="font-medium flex items-center gap-2"><Stethoscope size={16} /> Referring Physician</h4>
      <div className="space-y-2">
        <Label>Search NPI</Label>
        <div className="relative">
          <Input placeholder="Enter NPI or physician name..." onChange={(e) => handleNpiSearch(e.target.value)} data-testid="npi-search" />
          {npiSearching && <CircleNotch size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />}
        </div>
        {showNpiResults && npiSearchResults.length > 0 && (
          <div className="border rounded-md max-h-48 overflow-y-auto">
            {npiSearchResults.map((p) => (
              <button
                key={p.npi}
                type="button"
                onClick={() => handleSelectNpi(p)}
                className="w-full text-left p-2 hover:bg-slate-50 cursor-pointer border-b last:border-0"
              >
                <p className="font-medium text-sm">{p.name}</p>
                <p className="text-xs text-slate-500">NPI: {p.npi} {p.specialty && `• ${p.specialty}`}</p>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Physician NPI</Label>
          <Input value={formData.physician_npi} onChange={(e) => handleChange('physician_npi', e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-2">
          <Label>Physician Name</Label>
          <Input value={formData.physician_name} onChange={(e) => handleChange('physician_name', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Specialty</Label>
          <Input value={formData.physician_specialty} onChange={(e) => handleChange('physician_specialty', e.target.value)} />
        </div>
      </div>
    </div>

    <div className="border-t pt-4 space-y-4">
      <h4 className="font-medium flex items-center gap-2"><FirstAid size={16} /> Diagnosis Codes</h4>
      <div className="grid grid-cols-2 gap-4">
        <ICD10Search
          value={formData.primary_icd10_code}
          description={formData.primary_icd10_description}
          onChange={(code, desc) => { handleChange('primary_icd10_code', code); handleChange('primary_icd10_description', desc); }}
          label="Primary ICD-10"
          testId="patient-primary-icd10"
        />
        <ICD10Search
          value={formData.secondary_icd10_code}
          description={formData.secondary_icd10_description || ''}
          onChange={(code, desc) => { handleChange('secondary_icd10_code', code); handleChange('secondary_icd10_description', desc); }}
          label="Secondary ICD-10"
          testId="patient-secondary-icd10"
        />
      </div>
    </div>

    <div className="border-t pt-4 space-y-4">
      <h4 className="font-medium flex items-center gap-2"><ShieldCheck size={16} /> Prior Authorization</h4>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Auth Number</Label>
          <Input value={formData.prior_auth_number} onChange={(e) => handleChange('prior_auth_number', e.target.value)} className="font-mono" />
        </div>
        <div className="space-y-2">
          <Label>Start Date</Label>
          <Input type="date" value={formData.prior_auth_start_date} onChange={(e) => handleChange('prior_auth_start_date', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>End Date</Label>
          <Input type="date" value={formData.prior_auth_end_date} onChange={(e) => handleChange('prior_auth_end_date', e.target.value)} />
        </div>
      </div>
    </div>
  </TabsContent>
);

const PatientFormOtherTab = ({ formData, handleChange }) => (
  <TabsContent value="other" className="space-y-4 mt-4">
    <h4 className="font-medium">Emergency Contact</h4>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Contact Name</Label>
        <Input value={formData.emergency_contact_name} onChange={(e) => handleChange('emergency_contact_name', e.target.value)} />
      </div>
      <div className="space-y-2">
        <Label>Contact Phone</Label>
        <Input type="tel" value={formData.emergency_contact_phone} onChange={(e) => handleChange('emergency_contact_phone', e.target.value)} />
      </div>
    </div>
    <div className="space-y-2">
      <Label>Notes</Label>
      <Textarea value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} rows={4} data-testid="patient-notes" />
    </div>
  </TabsContent>
);

const PnmVerifyBadge = ({ patientId, verified, verifiedBy, verifiedAt, verifiedNotes, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const [notes, setNotes] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  // Friendly date for the verified-by line ("Feb 20, 2026" vs raw ISO).
  const whenLabel = verifiedAt ? formatDate(verifiedAt) : '';

  const submit = async (toggle) => {
    if (!patientId) return;
    setSaving(true);
    try {
      const { data } = await patientsApi.verifyPnm(patientId, {
        verified: toggle,
        notes: notes || undefined,
      });
      onChange?.(data);
      toast.success(toggle
        ? 'Patient marked as PNM-verified'
        : 'PNM verification cleared');
      setOpen(false);
      setNotes('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update verification');
    } finally {
      setSaving(false);
    }
  };

  if (verified) {
    return (
      <div
        className="mt-2 flex items-center gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm"
        data-testid="pnm-verified-badge"
      >
        <ShieldCheck size={18} className="text-emerald-700 shrink-0" weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-emerald-900">Verified in Ohio PNM</p>
          <p className="text-xs text-emerald-700 truncate">
            by {verifiedBy || 'unknown'}{whenLabel ? ` on ${whenLabel}` : ''}
            {verifiedNotes ? ` — ${verifiedNotes}` : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => submit(false)}
          disabled={saving}
          className="text-xs text-emerald-800 hover:text-emerald-950 underline shrink-0"
          data-testid="pnm-revoke-btn"
        >
          Revoke
        </button>
      </div>
    );
  }

  return (
    <div
      className="mt-2 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm"
      data-testid="pnm-unverified-badge"
    >
      <ShieldCheck size={18} className="text-amber-700 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-amber-900">Not verified in PNM</p>
        <p className="text-xs text-amber-700">
          Confirm Medicaid ID, name, DOB, and gender on the PNM portal to bypass mock-mode preflight false-positives (B1/B3/B4).
        </p>
      </div>
      {open ? (
        <div className="flex items-center gap-2">
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What you verified"
            className="text-xs rounded border border-amber-300 px-2 py-1 w-40"
            data-testid="pnm-verify-notes"
          />
          <button
            type="button"
            onClick={() => submit(true)}
            disabled={saving}
            className="text-xs bg-amber-700 hover:bg-amber-800 text-white px-3 py-1 rounded"
            data-testid="pnm-verify-confirm-btn"
          >
            {saving ? '…' : 'Mark verified'}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); setNotes(''); }}
            className="text-xs text-amber-800 hover:underline"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs bg-amber-700 hover:bg-amber-800 text-white px-3 py-1 rounded shrink-0"
          data-testid="pnm-verify-open-btn"
        >
          Mark as verified
        </button>
      )}
    </div>
  );
};


export const PatientFormModal = ({
  isOpen, onClose, onSubmit, saving, editingPatient, formData, handleChange,
  npiSearchResults, npiSearching, showNpiResults, handleNpiSearch, handleSelectNpi,
  initialTab = 'basic', focusField = null,
}) => {
  const [activeTab, setActiveTab] = React.useState(initialTab || 'basic');

  // Sync activeTab to initialTab whenever the modal opens
  React.useEffect(() => {
    if (isOpen) setActiveTab(initialTab || 'basic');
  }, [isOpen, initialTab]);

  // Focus + scroll the requested field once the modal renders
  React.useEffect(() => {
    if (!isOpen || !focusField) return;
    // Wait one tick for the tab content to render
    const id = setTimeout(() => {
      const el = document.querySelector(`[data-testid="patient-${focusField.replace(/_/g, '-')}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
        el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2'), 2400);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [isOpen, focusField, activeTab]);

  return (
  <Dialog open={isOpen} onOpenChange={onClose}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle style={{ fontFamily: 'Manrope' }}>
          {editingPatient ? 'Edit Patient' : 'Add New Patient'}
        </DialogTitle>
        <DialogDescription>Enter the patient's Medicaid and contact information</DialogDescription>
        {editingPatient ? (
          <PnmVerifyBadge
            patientId={editingPatient.id || formData.id}
            verified={formData.pnm_verified}
            verifiedBy={formData.pnm_verified_by}
            verifiedAt={formData.pnm_verified_at}
            verifiedNotes={formData.pnm_verified_notes}
            onChange={(updated) => {
              handleChange('pnm_verified', updated.pnm_verified);
              handleChange('pnm_verified_by', updated.pnm_verified_by);
              handleChange('pnm_verified_at', updated.pnm_verified_at);
              handleChange('pnm_verified_notes', updated.pnm_verified_notes);
            }}
          />
        ) : null}
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="basic"><User size={14} className="mr-1" /> Basic</TabsTrigger>
            <TabsTrigger value="insurance"><ShieldCheck size={14} className="mr-1" /> Insurance</TabsTrigger>
            <TabsTrigger value="clinical"><Stethoscope size={14} className="mr-1" /> Clinical</TabsTrigger>
            <TabsTrigger value="other"><FileText size={14} className="mr-1" /> Other</TabsTrigger>
          </TabsList>
          <PatientFormBasicTab formData={formData} handleChange={handleChange} />
          <PatientFormInsuranceTab formData={formData} handleChange={handleChange} />
          <PatientFormClinicalTab
            formData={formData}
            handleChange={handleChange}
            npiSearchResults={npiSearchResults}
            npiSearching={npiSearching}
            showNpiResults={showNpiResults}
            handleNpiSearch={handleNpiSearch}
            handleSelectNpi={handleSelectNpi}
          />
          <PatientFormOtherTab formData={formData} handleChange={handleChange} />
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" className="bg-indigo-900 hover:bg-indigo-800" disabled={saving} data-testid="save-patient-btn">
            {saving
              ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : editingPatient ? 'Save Changes' : 'Add Patient'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
  );
};
